import React, { FC, useEffect, useState } from 'react';
import { Table } from 'antd';
import type { TableProps } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { EmptyState, HorizontalScrollbar } from 'common/components';
import { UserHooks, useSearchParamsHook } from 'common/hooks';
import { EOrderDirection, ERoute, ESearchParams } from 'common/const';
import { useAppSelector } from 'app/store/store.hooks';
import { getCatalogEmptyStateContent, renderCatalogGoodsListRecords } from 'entities/Catalog/Catalog.helper';
import { IGood, IGoodListPayload } from 'entities/Goods/Goods.models';
import { selectCategory, selectIsCategoryLoading } from 'entities/Category/Category.slice';
import { selectGoodHasMore, selectGoodList, selectIsGoodLoading } from 'entities/Goods/Good.slice';
import { selectCurrentUser } from 'entities/User/User.slice';
import { selectIsPropertyLoading } from 'entities/Property/Property.slice';

interface IComponentProps {
  styles: Record<string, React.CSSProperties>;
  catalogId: number;
  categoryId?: number;
  loading: boolean;
  payload: IGoodListPayload;
  onShowAllAssortmentClick: () => void;
  onSort: (direction?: string, field?: string) => void;
}

export const CatalogGoodsTable: FC<IComponentProps> = ({
  styles,
  catalogId,
  categoryId,
  loading,
  payload,
  onShowAllAssortmentClick,
  onSort,
}) => {
  // category
  const category = useAppSelector(selectCategory);
  const isCategoryLoading = useAppSelector(selectIsCategoryLoading);
  // good
  const goodList = useAppSelector(selectGoodList);
  const goodHasMore = useAppSelector(selectGoodHasMore);
  const isGoodLoading = useAppSelector(selectIsGoodLoading);
  // property
  const isPropertyLoading = useAppSelector(selectIsPropertyLoading);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [scrollableContainer, setScrollableContainer] = useState<HTMLDivElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isSelfRegistered } = UserHooks.useIsSelfRegistered();
  const { getSearchParam } = useSearchParamsHook();

  useEffect(() => {
    if (!loading) {
      const container = document.querySelector('.ant-table-body') as HTMLDivElement | null;
      setScrollableContainer(container);
    }
  }, [loading, goodList]);

  const listLoading = isCategoryLoading || isPropertyLoading || isGoodLoading;
  const search = getSearchParam(ESearchParams.Search);
  const columns = renderCatalogGoodsListRecords({
    category: categoryId ? category : null,
    user: currentUser,
    searchNotEmpty: search !== null,
  });
  const { emptyTitle, emptyDescription, emptyBackBtnTitle } = getCatalogEmptyStateContent({
    payload,
    categoryId,
    account: currentUser?.account,
  });

  const handleBackClick = (): void => {
    if (categoryId && category?.parentIds?.length) {
      navigate(`${ERoute.Catalog}/${catalogId}/${category.parentIds[category.parentIds.length - 1]}`);
    } else {
      navigate(`${ERoute.Catalog}/${catalogId}`);
    }
  };

  const handleSortChange: TableProps<IGood>['onChange'] = (p, f, s): void => {
    // @ts-ignore
    const orderDirection = !s.order ? undefined : s.order === 'ascend' ? EOrderDirection.ASC : EOrderDirection.DESC;
    // @ts-ignore
    const orderField = !s.order ? undefined : s.columnKey;

    onSort(orderDirection, orderField);
  };

  const navigateToGood = (id: number): void => {
    if (search !== null) {
      navigate(`${ERoute.Catalog}/${catalogId}/good/${id}?search=${search}`, { state: location.state });
    }

    if (category) {
      navigate(`${ERoute.Catalog}/${catalogId}/${category.id}/good/${id}`, { state: location.state });
    }
  };

  return (
    <div className="flex direction-column flex-1">
      <div style={{ position: 'sticky', top: `${styles.tableHeader.top}px`, zIndex: 10 }}>
        <HorizontalScrollbar scrollableContainer={scrollableContainer} />
      </div>

      <Table
        tableLayout="auto"
        className={`
          catalog-goods-list
          table-hover
          ${!!goodList.length && !goodHasMore && 'table-end-of-list'}
          ${!goodList.length && 'table-empty'}
        `}
        dataSource={goodList.map((goods) => ({ key: goods.id, ...goods }))}
        columns={columns}
        pagination={false}
        loading={listLoading}
        showHeader={!!goodList.length}
        onChange={handleSortChange}
        onRow={({ id }) => ({
          onClick: () => navigateToGood(id),
        })}
        style={
          {
            '--header-top': `${styles.tableHeader.top}px`,
          } as React.CSSProperties
        }
        locale={{
          emptyText: (
            <EmptyState
              title={emptyTitle}
              description={emptyDescription}
              btnTitle={emptyBackBtnTitle}
              onClick={!isSelfRegistered && payload.isAvailableForCustomer ? onShowAllAssortmentClick : handleBackClick}
            />
          ),
          triggerDesc: 'Сортировать по убыванию',
          triggerAsc: 'Сортировать по возрастанию',
          cancelSort: 'Отменить сортировку',
        }}
        scroll={{ x: true, y: '100%' }}
      />
    </div>
  );
};
